import {Question, Quiz} from "../models/Quiz";
import {normalizeDateKey} from "../utils/date";

/**
 * Base URL of the headless WordPress site, e.g. "https://cms.geared.io".
 * When unset, the app falls back to the bundled static quiz data so the
 * game keeps working until a hosted CMS exists.
 */
const API_BASE: string | undefined = process.env.REACT_APP_QUIZ_API_URL;

interface ApiQuestion {
    questionNumber: number;
    soundUrl: string;
    soundUrl1s?: string | null;
    option1: string;
    option2: string;
    option3: string;
    option4: string;
    ans: number;
    factoid: string;
}

interface StaticQuestion {
    questionNumber: number;
    soundFile: string;
    soundFile1s?: string;
    option1: string;
    option2: string;
    option3: string;
    option4: string;
    ans: number;
    factoid: string;
}

function toQuestion(q: {
    questionNumber: number;
    option1: string;
    option2: string;
    option3: string;
    option4: string;
    ans: number;
    factoid: string;
}, soundUrl: string, soundUrl1s?: string): Question {
    return {
        questionNumber: q.questionNumber,
        soundUrl,
        soundUrl1s: soundUrl1s || undefined,
        options: [q.option1, q.option2, q.option3, q.option4],
        answer: q.ans - 1,
        factoid: q.factoid,
    };
}

async function fetchQuizFromApi(dateKey: string): Promise<Quiz | null> {
    const response = await fetch(`${API_BASE}/wp-json/geared/v1/quiz?date=${dateKey}`);
    if (response.status === 404) {
        return null;
    }
    if (!response.ok) {
        throw new Error(`Quiz API responded ${response.status}`);
    }
    const payload = await response.json();
    return {
        date: payload.date,
        theme: payload.theme ?? undefined,
        questions: (payload.questions as ApiQuestion[]).map((q) =>
            toQuestion(q, q.soundUrl, q.soundUrl1s ?? undefined)),
    };
}

/** Deterministic hash so every player recycles the same day. */
function dateHash(dateKey: string): number {
    let hash = 0;
    for (let i = 0; i < dateKey.length; i++) {
        hash = (hash * 31 + dateKey.charCodeAt(i)) >>> 0;
    }
    return hash;
}

async function fetchQuizFromStaticData(dateKey: string, recycleWhenMissing = false): Promise<Quiz | null> {
    // Lazily imported so the (large) static data file stays out of the
    // critical bundle path once the API takes over.
    const {data} = await import("../assets/quiz-data");
    const days = data as Array<{date: string; questions: StaticQuestion[]}>;
    let day = days.find((d) => normalizeDateKey(d.date) === dateKey);
    if (!day && recycleWhenMissing && days.length) {
        // Emergency mode (CMS unreachable, date past the bundled range):
        // recycle a bundled day, chosen deterministically from the date so
        // all players still get the identical quiz.
        day = days[dateHash(dateKey) % days.length];
    }
    if (!day) {
        return null;
    }
    return {
        date: dateKey,
        questions: day.questions.map((q) => toQuestion(
            q,
            `/sounds/${q.soundFile}`,
            q.soundFile1s ? `/sounds/${q.soundFile1s}` : undefined,
        )),
    };
}

/**
 * Fetches the quiz for the given ISO date key; null when no quiz exists.
 *
 * A CMS *404* means "genuinely no quiz" and returns null, but a network
 * failure (offline, DNS, blocked request) falls back to the bundled static
 * data so a connectivity blip doesn't read as "the game has no content".
 */
export async function fetchQuiz(dateKey: string): Promise<Quiz | null> {
    if (!API_BASE) {
        return fetchQuizFromStaticData(dateKey);
    }
    try {
        return await fetchQuizFromApi(dateKey);
    } catch (e) {
        console.warn("quiz API unreachable, using bundled data", e);
        return fetchQuizFromStaticData(dateKey, true);
    }
}
